import {
	SkinType,
	PatternType,
	COLOR_SKINS,
	FLAG_SKINS,
	type PlayerSkin,
	type ColorSkin,
	type PatternSkin,
	type FlagSkin,
	type SkinData,
	serializeSkin,
	loadSkinFromStorage,
	saveSkinToStorage,
	hexToNumber,
	getSkinsByCollection,
	getCollectionsInOrder,
	// Shared accessory system
	AccessoryType,
	Canvas2DAdapter,
	drawAccessory as drawSharedAccessory,
	getAccessoryPosition,
} from '@wooorms/shared';
import { EyeProtectionSettings } from './EyeProtectionToggle';

/**
 * Tab types for skin selector
 */
export enum SkinTab {
	COLORS = 'colors',
	PATTERNS = 'patterns',
	FLAGS = 'flags',
}

/**
 * Event handlers for SkinSelector
 */
export interface SkinSelectorEvents {
	onSkinChange: (skin: PlayerSkin) => void;
}

/**
 * SkinSelector - UI component for selecting worm skins
 *
 * Features:
 * - Three tabs: Colors, Patterns, Flags
 * - Visual preview of worm with selected skin
 * - Saves selection to localStorage
 */
export class SkinSelector {
	/** Container element */
	private container: HTMLDivElement;

	/** Tab buttons */
	private tabButtons: Map<SkinTab, HTMLButtonElement> = new Map();

	/** Content panels for each tab */
	private tabPanels: Map<SkinTab, HTMLDivElement> = new Map();

	/** Preview canvas */
	private previewCanvas: HTMLCanvasElement;
	private previewCtx: CanvasRenderingContext2D | null;

	/** Currently selected skin */
	private selectedSkin: PlayerSkin;

	/** Currently selected accessory for preview */
	private selectedAccessory: string = 'none';

	/** Currently active tab */
	private activeTab: SkinTab = SkinTab.COLORS;

	/** Event handlers */
	private events: SkinSelectorEvents;

	/** Animation frame for preview */
	private animationFrame: number | null = null;
	private animationTime: number = 0;

	/** Animated pattern button canvases for live preview */
	private animatedButtonCanvases: Map<
		HTMLCanvasElement,
		{ skin: PatternSkin; ctx: CanvasRenderingContext2D }
	> = new Map();

	/** Color button canvases for wave animation */
	private colorButtonCanvases: Map<
		HTMLCanvasElement,
		{ skin: ColorSkin; ctx: CanvasRenderingContext2D }
	> = new Map();

	/** Flag button canvases for wave animation */
	private flagButtonCanvases: Map<
		HTMLCanvasElement,
		{ skin: FlagSkin; ctx: CanvasRenderingContext2D }
	> = new Map();

	/** Last time button canvases were updated (for throttling) */
	private lastButtonUpdate: number = 0;

	/** Eye protection mode - slows/stops animations */
	private _eyeProtectionEnabled: boolean = false;

	/** Check if eye protection is enabled (uses EyeProtectionSettings) */
	private checkEyeProtection(): boolean {
		return EyeProtectionSettings.getInstance().isEnabled();
	}

	/**
	 * Mute a color for Eye Shield+ mode - reduces saturation and brightness
	 * Works with hex (#rrggbb), rgb(r, g, b), and hsl(h, s%, l%) formats
	 */
	private muteColor(color: string): string {
		// Skip muting if not in Eye Shield+ mode
		if (!EyeProtectionSettings.getInstance().shouldMuteColors()) {
			return color;
		}

		// Muting factors: reduce saturation by 50%, brightness by 25%
		const saturationFactor = 0.5;
		const brightnessFactor = 0.75;

		// Handle HSL format directly
		if (color.startsWith('hsl(')) {
			const match = color.match(/hsl\((\d+),\s*(\d+)%?,\s*(\d+)%?\)/);
			if (match) {
				const h = parseInt(match[1] ?? '0', 10);
				const s = parseInt(match[2] ?? '0', 10) * saturationFactor;
				const l = parseInt(match[3] ?? '0', 10) * brightnessFactor;
				return `hsl(${h}, ${Math.round(s)}%, ${Math.round(l)}%)`;
			}
		}

		// Handle RGB format
		let r: number, g: number, b: number;
		if (color.startsWith('rgb(') || color.startsWith('rgba(')) {
			const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
			if (match) {
				r = parseInt(match[1] ?? '0', 10);
				g = parseInt(match[2] ?? '0', 10);
				b = parseInt(match[3] ?? '0', 10);
			} else {
				return color;
			}
		} else if (color.startsWith('#')) {
			// Handle hex format
			r = parseInt(color.slice(1, 3), 16);
			g = parseInt(color.slice(3, 5), 16);
			b = parseInt(color.slice(5, 7), 16);
		} else {
			return color; // Unknown format, return unchanged
		}

		// Convert RGB to HSL
		const rNorm = r / 255;
		const gNorm = g / 255;
		const bNorm = b / 255;
		const max = Math.max(rNorm, gNorm, bNorm);
		const min = Math.min(rNorm, gNorm, bNorm);
		let h = 0;
		let s = 0;
		const l = (max + min) / 2;

		if (max !== min) {
			const d = max - min;
			s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
			switch (max) {
				case rNorm:
					h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6;
					break;
				case gNorm:
					h = ((bNorm - rNorm) / d + 2) / 6;
					break;
				case bNorm:
					h = ((rNorm - gNorm) / d + 4) / 6;
					break;
			}
		}

		// Apply muting: reduce saturation and brightness
		const mutedS = s * saturationFactor;
		const mutedL = l * brightnessFactor;

		// Convert back to RGB
		const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
			if (s === 0) {
				const gray = Math.round(l * 255);
				return [gray, gray, gray];
			}
			const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
			const p = 2 * l - q;
			const hue2rgb = (p: number, q: number, t: number): number => {
				if (t < 0) t += 1;
				if (t > 1) t -= 1;
				if (t < 1/6) return p + (q - p) * 6 * t;
				if (t < 1/2) return q;
				if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
				return p;
			};
			return [
				Math.round(hue2rgb(p, q, h + 1/3) * 255),
				Math.round(hue2rgb(p, q, h) * 255),
				Math.round(hue2rgb(p, q, h - 1/3) * 255)
			];
		};

		const [newR, newG, newB] = hslToRgb(h, mutedS, mutedL);
		return `rgb(${newR}, ${newG}, ${newB})`;
	}

	/**
	 * Add responsive CSS styles for skin selector grids (once globally)
	 */
	private addResponsiveStyles(): void {
		if (document.getElementById('skin-selector-grid-styles')) return;

		const styles = document.createElement('style');
		styles.id = 'skin-selector-grid-styles';
		styles.textContent = `
			/* Base grid: responsive with minmax */
			.skin-grid {
				display: grid;
				grid-template-columns: repeat(auto-fill, minmax(95px, 1fr));
				gap: 10px;
				padding: 8px 4px;
			}

			/* Skin button base responsive styles */
			.skin-btn {
				width: 100%;
				aspect-ratio: 1.15;
				border-radius: 12px;
				border: 3px solid transparent;
				cursor: pointer;
				transition: all 0.15s ease;
				background: linear-gradient(145deg, rgba(40, 40, 55, 0.9), rgba(25, 25, 35, 0.9));
				padding: 0;
				position: relative;
				overflow: hidden;
				box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
				display: flex;
				flex-direction: column;
			}

			.skin-btn canvas {
				display: block;
				width: 100%;
				flex: 1;
				border-radius: 9px 9px 0 0;
				object-fit: cover;
			}

			.skin-btn .skin-label {
				font-size: 10px;
				font-weight: 600;
				color: #d0d0d0;
				text-align: center;
				padding: 5px 4px;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				transition: all 0.15s;
				text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
				background: linear-gradient(to bottom, rgba(30, 30, 45, 0.6), rgba(20, 20, 30, 0.8));
				border-radius: 0 0 9px 9px;
				min-height: 24px;
				display: flex;
				align-items: center;
				justify-content: center;
			}

			/* Collection header */
			.collection-header {
				display: flex;
				align-items: center;
				gap: 8px;
				padding: 10px 8px 6px 8px;
				margin-top: 4px;
				border-bottom: 1px solid rgba(78, 205, 196, 0.15);
				cursor: pointer;
				user-select: none;
				transition: background 0.15s ease;
			}

			.collection-header:hover {
				background: rgba(78, 205, 196, 0.05);
			}

			.collection-header .expand-icon {
				font-size: 12px;
				transition: transform 0.2s ease;
				margin-left: auto;
				color: rgba(78, 205, 196, 0.6);
			}

			.collection-header.collapsed .expand-icon {
				transform: rotate(-90deg);
			}

			.collection-grid.collapsed {
				display: none !important;
			}

			/* Mobile: 3 columns with larger touch targets */
			@media (max-width: 480px) {
				.skin-grid {
					grid-template-columns: repeat(3, 1fr);
					gap: 8px;
				}
				.skin-btn {
					border-radius: 10px;
				}
				.skin-btn .skin-label {
					font-size: 9px;
					padding: 4px 2px;
					min-height: 20px;
				}
			}

			/* Small tablet: 4 columns */
			@media (min-width: 481px) and (max-width: 600px) {
				.skin-grid {
					grid-template-columns: repeat(4, 1fr);
					gap: 8px;
				}
			}

			/* Tablet: 5 columns */
			@media (min-width: 601px) and (max-width: 900px) {
				.skin-grid {
					grid-template-columns: repeat(5, 1fr);
					gap: 10px;
				}
			}

			/* Desktop: 6 columns */
			@media (min-width: 901px) and (max-width: 1200px) {
				.skin-grid {
					grid-template-columns: repeat(6, 1fr);
					gap: 10px;
				}
			}

			/* Large desktop: 7 columns */
			@media (min-width: 1201px) and (max-width: 1600px) {
				.skin-grid {
					grid-template-columns: repeat(7, 1fr);
					gap: 12px;
				}
			}

			/* Extra large: 8 columns */
			@media (min-width: 1601px) {
				.skin-grid {
					grid-template-columns: repeat(8, 1fr);
					gap: 12px;
				}
			}
		`;
		document.head.appendChild(styles);
	}

	constructor(events: SkinSelectorEvents) {
		this.events = events;
		this.selectedSkin = loadSkinFromStorage();

		// Create container
		this.container = document.createElement('div');
		this.container.className = 'skin-selector';
		this.container.style.cssText = `
      width: 100%;
      margin-top: 20px;
    `;

		// Add responsive grid styles (once globally)
		this.addResponsiveStyles();

		// Create preview section - impressive hero preview
		const previewSection = document.createElement('div');
		previewSection.style.cssText = `
      text-align: center;
      margin-bottom: 20px;
    `;

		// Create large preview canvas - hero-sized for impact
		this.previewCanvas = document.createElement('canvas');
		this.previewCanvas.width = 700;
		this.previewCanvas.height = 260;
		this.previewCanvas.style.cssText = `
      display: block;
      margin: 0 auto;
      border-radius: 16px;
      background: transparent;
      width: 100%;
      height: auto;
      aspect-ratio: 3 / 1;
    `;
		this.previewCtx = this.previewCanvas.getContext('2d');
		previewSection.appendChild(this.previewCanvas);

		this.container.appendChild(previewSection);

		// Create tabs
		this.createTabs();

		// Create tab panels
		this.createColorPanel();
		this.createPatternPanel();
		this.createFlagPanel();

		// Show initial tab
		this.switchTab(SkinTab.COLORS);

		// Select the loaded skin in the UI
		this.selectSkinInUI(this.selectedSkin);

		// Start preview animation
		this.startPreviewAnimation();
	}

	/**
	 * Create tab buttons
	 */
	private createTabs(): void {
		const tabContainer = document.createElement('div');
		tabContainer.style.cssText = `
      display: none; /* Hidden - using ConnectionUI tabs instead */
      gap: 8px;
      margin-bottom: 16px;
      padding: 6px;
      background: linear-gradient(145deg, rgba(20, 20, 35, 0.8), rgba(15, 15, 25, 0.8));
      border-radius: 14px;
      border: 1px solid rgba(78, 205, 196, 0.1);
    `;

		const tabs: { id: SkinTab; label: string; icon: string; color: string }[] = [
			{ id: SkinTab.COLORS, label: 'Colors', icon: '🎨', color: '#ff6b6b' },
			{ id: SkinTab.PATTERNS, label: 'Patterns', icon: '✨', color: '#ffd93d' },
			{ id: SkinTab.FLAGS, label: 'Flags', icon: '🏁', color: '#6bcb77' },
		];

		tabs.forEach((tab) => {
			const button = document.createElement('button');
			button.innerHTML = `<span style="margin-right: 6px;">${tab.icon}</span>${tab.label}`;
			button.style.cssText = `
        flex: 1;
        padding: 12px 14px;
        font-size: 13px;
        font-weight: 600;
        border: none;
        border-radius: 10px;
        background: transparent;
        color: #888;
        cursor: pointer;
        transition: all 0.25s ease;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
        letter-spacing: 0.3px;
      `;

			button.addEventListener('click', () => {
				this.switchTab(tab.id);
			});

			button.addEventListener('mouseover', () => {
				if (this.activeTab !== tab.id) {
					button.style.background = `linear-gradient(145deg, ${tab.color}15, ${tab.color}08)`;
					button.style.color = '#ccc';
					button.style.transform = 'translateY(-1px)';
				}
			});

			button.addEventListener('mouseout', () => {
				if (this.activeTab !== tab.id) {
					button.style.background = 'transparent';
					button.style.color = '#888';
					button.style.transform = 'translateY(0)';
				}
			});

			this.tabButtons.set(tab.id, button);
			tabContainer.appendChild(button);
		});

		this.container.appendChild(tabContainer);
	}

	/**
	 * Create color selection panel - simple grid of all colors
	 */
	private createColorPanel(): void {
		const panel = document.createElement('div');
		panel.style.cssText = `
      display: none;
      padding: 10px;
      border-radius: 12px;
    `;

		// Simple grid with all colors - uses CSS Grid for responsive layout
		const grid = document.createElement('div');
		grid.className = 'skin-grid';

		COLOR_SKINS.forEach((skin) => {
			const button = this.createColorButton(skin, () => {
				this.selectSkin(skin);
			});
			grid.appendChild(button);
		});

		panel.appendChild(grid);

		this.tabPanels.set(SkinTab.COLORS, panel);
		this.container.appendChild(panel);
	}

	/**
	 * Create pattern selection panel organized by kid-friendly collections
	 */
	private createPatternPanel(): void {
		const panel = document.createElement('div');
		panel.style.cssText = `
      display: none;
      padding: 10px;
      border-radius: 12px;
    `;

		// Use the new collection system - kid-friendly organization!
		const collections = getCollectionsInOrder();

		collections.forEach((collectionInfo, index) => {
			const collectionSkins = getSkinsByCollection(collectionInfo.id);
			if (collectionSkins.length === 0) return;

			// Collection header with icon, name, and expand/collapse
			const header = document.createElement('div');
			header.className = 'collection-header';

			const headerIcon = document.createElement('span');
			headerIcon.textContent = collectionInfo.icon;
			headerIcon.style.cssText = `font-size: 16px;`;

			const headerText = document.createElement('span');
			headerText.textContent = collectionInfo.name;
			headerText.style.cssText = `
        font-size: 11px;
        font-weight: 600;
        color: rgba(78, 205, 196, 0.9);
        text-transform: uppercase;
        letter-spacing: 1px;
      `;

			const headerCount = document.createElement('span');
			headerCount.textContent = `(${collectionSkins.length})`;
			headerCount.style.cssText = `
        font-size: 10px;
        color: rgba(78, 205, 196, 0.5);
        margin-left: 4px;
      `;

			const expandIcon = document.createElement('span');
			expandIcon.className = 'expand-icon';
			expandIcon.textContent = '▼';

			header.appendChild(headerIcon);
			header.appendChild(headerText);
			header.appendChild(headerCount);
			header.appendChild(expandIcon);
			panel.appendChild(header);

			// Skins grid for this collection - uses CSS Grid
			const grid = document.createElement('div');
			grid.className = 'skin-grid collection-grid';

			collectionSkins.forEach((skin) => {
				const button = this.createPatternButton(skin, () => {
					this.selectSkin(skin);
				});
				grid.appendChild(button);
			});

			panel.appendChild(grid);

			// Start with first 2 collections expanded, rest collapsed on desktop
			// On mobile (detected via matchMedia), keep all expanded for easier scrolling
			const isMobile = window.matchMedia('(max-width: 600px)').matches;
			if (!isMobile && index >= 2) {
				header.classList.add('collapsed');
				grid.classList.add('collapsed');
			}

			// Toggle collapse on header click
			header.addEventListener('click', () => {
				const isCollapsed = header.classList.contains('collapsed');
				if (isCollapsed) {
					header.classList.remove('collapsed');
					grid.classList.remove('collapsed');
				} else {
					header.classList.add('collapsed');
					grid.classList.add('collapsed');
				}
			});
		});

		this.tabPanels.set(SkinTab.PATTERNS, panel);
		this.container.appendChild(panel);
	}

	/**
	 * Create flag selection panel with regional organization
	 */
	private createFlagPanel(): void {
		const panel = document.createElement('div');
		panel.style.cssText = `
      display: none;
      padding: 10px;
      border-radius: 12px;
    `;

		// Group flags by region
		const regions: { name: string; icon: string; ids: string[] }[] = [
			{
				name: 'Europe', icon: '🏰', ids: [
					'flag-uk', 'flag-germany', 'flag-france', 'flag-spain', 'flag-italy', 'flag-greece',
					'flag-netherlands', 'flag-sweden', 'flag-poland', 'flag-norway', 'flag-finland', 'flag-ireland',
					'flag-austria', 'flag-denmark', 'flag-switzerland', 'flag-belgium', 'flag-portugal', 'flag-ukraine',
					'flag-russia', 'flag-cyprus', 'flag-czech', 'flag-romania', 'flag-hungary', 'flag-croatia',
					'flag-serbia', 'flag-bulgaria', 'flag-slovakia', 'flag-slovenia', 'flag-iceland', 'flag-luxembourg',
					'flag-scotland', 'flag-wales'
				]
			},
			{
				name: 'Americas', icon: '🗽', ids: [
					'flag-usa', 'flag-canada', 'flag-brazil', 'flag-mexico', 'flag-argentina',
					'flag-colombia', 'flag-chile', 'flag-peru', 'flag-venezuela', 'flag-ecuador',
					'flag-uruguay', 'flag-paraguay', 'flag-bolivia'
				]
			},
			{
				name: 'Central America & Caribbean', icon: '🏝️', ids: [
					'flag-jamaica', 'flag-cuba', 'flag-costa-rica', 'flag-panama', 'flag-puerto-rico'
				]
			},
			{
				name: 'Asia', icon: '🏯', ids: [
					'flag-japan', 'flag-south-korea', 'flag-china', 'flag-india', 'flag-turkey',
					'flag-thailand', 'flag-vietnam', 'flag-indonesia', 'flag-philippines', 'flag-malaysia',
					'flag-singapore', 'flag-pakistan', 'flag-bangladesh'
				]
			},
			{
				name: 'Middle East', icon: '🕌', ids: [
					'flag-saudi-arabia', 'flag-uae', 'flag-israel', 'flag-iran', 'flag-iraq'
				]
			},
			{
				name: 'Africa', icon: '🌍', ids: [
					'flag-south-africa', 'flag-egypt', 'flag-nigeria', 'flag-morocco', 'flag-kenya',
					'flag-ghana', 'flag-ethiopia', 'flag-algeria', 'flag-tunisia', 'flag-senegal'
				]
			},
			{
				name: 'Oceania', icon: '🏄', ids: [
					'flag-australia', 'flag-new-zealand', 'flag-fiji', 'flag-papua-new-guinea'
				]
			},
		];

		regions.forEach((region, index) => {
			const regionFlags = FLAG_SKINS.filter((skin) =>
				region.ids.includes(skin.id)
			);
			if (regionFlags.length === 0) return;

			// Region header with expand/collapse
			const header = document.createElement('div');
			header.className = 'collection-header';

			const headerIcon = document.createElement('span');
			headerIcon.textContent = region.icon;
			headerIcon.style.cssText = `font-size: 14px;`;

			const headerText = document.createElement('span');
			headerText.textContent = region.name;
			headerText.style.cssText = `
        font-size: 11px;
        font-weight: 600;
        color: rgba(78, 205, 196, 0.9);
        text-transform: uppercase;
        letter-spacing: 1px;
      `;

			const headerCount = document.createElement('span');
			headerCount.textContent = `(${regionFlags.length})`;
			headerCount.style.cssText = `
        font-size: 10px;
        color: rgba(78, 205, 196, 0.5);
        margin-left: 4px;
      `;

			const expandIcon = document.createElement('span');
			expandIcon.className = 'expand-icon';
			expandIcon.textContent = '▼';

			header.appendChild(headerIcon);
			header.appendChild(headerText);
			header.appendChild(headerCount);
			header.appendChild(expandIcon);
			panel.appendChild(header);

			// Flags grid for this region - uses CSS Grid
			const grid = document.createElement('div');
			grid.className = 'skin-grid collection-grid';

			regionFlags.forEach((skin) => {
				const button = this.createFlagButton(skin, () => {
					this.selectSkin(skin);
				});
				grid.appendChild(button);
			});

			panel.appendChild(grid);

			// Start with first 2 regions expanded, rest collapsed on desktop
			const isMobile = window.matchMedia('(max-width: 600px)').matches;
			if (!isMobile && index >= 2) {
				header.classList.add('collapsed');
				grid.classList.add('collapsed');
			}

			// Toggle collapse on header click
			header.addEventListener('click', () => {
				const isCollapsed = header.classList.contains('collapsed');
				if (isCollapsed) {
					header.classList.remove('collapsed');
					grid.classList.remove('collapsed');
				} else {
					header.classList.add('collapsed');
					grid.classList.add('collapsed');
				}
			});
		});

		this.tabPanels.set(SkinTab.FLAGS, panel);
		this.container.appendChild(panel);
	}

	/**
	 * Create a color skin button with mini worm preview
	 */
	private createColorButton(skin: ColorSkin, onClick: () => void): HTMLButtonElement {
		const button = document.createElement('button');
		button.className = 'skin-btn';
		button.dataset.skinId = skin.id;
		button.title = skin.name;

		// Create mini canvas for worm preview (responsive sizing handled by CSS)
		const canvas = document.createElement('canvas');
		canvas.width = 110;
		canvas.height = 58;
		button.appendChild(canvas);

		// Draw mini worm on canvas
		this.drawMiniColorWorm(canvas, skin, 0);

		// Register canvas for wave animation
		const ctx = canvas.getContext('2d');
		if (ctx) {
			this.colorButtonCanvases.set(canvas, { skin, ctx });
		}

		// Add label with CSS class
		const label = document.createElement('div');
		label.className = 'skin-label';
		label.textContent = skin.name;
		button.appendChild(label);

		button.addEventListener('click', onClick);
		button.addEventListener('mouseover', () => {
			button.style.transform = 'scale(1.06) translateY(-2px)';
			button.style.boxShadow = `0 4px 16px rgba(0, 0, 0, 0.4), 0 0 20px ${skin.primaryColor}30`;
			label.style.color = '#fff';
			label.style.background = 'linear-gradient(to bottom, rgba(40, 40, 60, 0.8), rgba(30, 30, 45, 0.9))';
		});
		button.addEventListener('mouseout', () => {
			button.style.transform = 'scale(1)';
			button.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
			label.style.color = '#d0d0d0';
			label.style.background = 'linear-gradient(to bottom, rgba(30, 30, 45, 0.6), rgba(20, 20, 30, 0.8))';
		});

		return button;
	}

	/**
	 * Draw a mini color worm preview on canvas with wave animation
	 */
	private drawMiniColorWorm(canvas: HTMLCanvasElement, skin: ColorSkin, animTime: number = 0): void {
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		const width = canvas.width;
		const height = canvas.height;
		const time = animTime / 1000;

		// Clear canvas with subtle gradient
		const bgGradient = ctx.createLinearGradient(0, 0, 0, height);
		bgGradient.addColorStop(0, 'rgba(25, 25, 40, 1)');
		bgGradient.addColorStop(1, 'rgba(15, 15, 25, 1)');
		ctx.fillStyle = bgGradient;
		ctx.fillRect(0, 0, width, height);

		// Draw mini worm segments - scaled to fit new canvas size
		const segmentCount = 7;
		const segmentRadius = 10;
		const segmentSpacing = 13;
		const startX = 16;
		const centerY = height / 2;

		for (let i = segmentCount - 1; i >= 0; i--) {
			const x = startX + i * segmentSpacing;
			// Wave animation for lively feel
			const waveOffset = Math.sin(time * 2.5 + i * 0.4) * 2;
			const y = centerY + waveOffset;
			const t = i / (segmentCount - 1);

			// Get darker color for tail, then apply Eye Shield+ muting if enabled
			const rawColor = this.applyGradientDarkening(skin.primaryColor, t);
			const color = this.muteColor(rawColor);

			// Draw segment shadow
			ctx.beginPath();
			ctx.arc(x + 1, y + 2, segmentRadius - i * 0.25, 0, Math.PI * 2);
			ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
			ctx.fill();

			// Draw segment
			ctx.beginPath();
			ctx.arc(x, y, segmentRadius - i * 0.25, 0, Math.PI * 2);
			ctx.fillStyle = color;
			ctx.fill();

			// Add highlight
			ctx.beginPath();
			ctx.arc(x - 2, y - 2, segmentRadius * 0.25, 0, Math.PI * 2);
			ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
			ctx.fill();
		}

		// Draw mini eyes on head with wave offset
		const headX = startX;
		const headWave = Math.sin(time * 2.5) * 2;
		const headY = centerY + headWave;

		// Eyes
		ctx.beginPath();
		ctx.arc(headX - 1, headY - 3.5, 2.8, 0, Math.PI * 2);
		ctx.fillStyle = '#ffffff';
		ctx.fill();
		ctx.beginPath();
		ctx.arc(headX - 1.5, headY - 3.5, 1.4, 0, Math.PI * 2);
		ctx.fillStyle = '#111111';
		ctx.fill();

		ctx.beginPath();
		ctx.arc(headX - 1, headY + 3.5, 2.8, 0, Math.PI * 2);
		ctx.fillStyle = '#ffffff';
		ctx.fill();
		ctx.beginPath();
		ctx.arc(headX - 1.5, headY + 3.5, 1.4, 0, Math.PI * 2);
		ctx.fillStyle = '#111111';
		ctx.fill();
	}

	/**
	 * Create a pattern skin button with mini worm preview
	 */
	private createPatternButton(skin: PatternSkin, onClick: () => void): HTMLButtonElement {
		const button = document.createElement('button');
		button.className = 'skin-btn';
		button.dataset.skinId = skin.id;
		button.title = skin.name;

		// Create mini canvas for worm preview (responsive sizing handled by CSS)
		const canvas = document.createElement('canvas');
		canvas.width = 110;
		canvas.height = 58;
		button.appendChild(canvas);

		// Draw mini worm on canvas
		this.drawMiniWorm(canvas, skin, 0);

		// Register ALL pattern canvases for wave animation (not just animated patterns)
		const ctx = canvas.getContext('2d');
		if (ctx) {
			this.animatedButtonCanvases.set(canvas, { skin, ctx });
		}

		// Add label with CSS class
		const label = document.createElement('div');
		label.className = 'skin-label';
		label.textContent = skin.name;
		button.appendChild(label);

		button.addEventListener('click', onClick);
		button.addEventListener('mouseover', () => {
			button.style.transform = 'scale(1.06) translateY(-2px)';
			button.style.boxShadow = `0 4px 16px rgba(0, 0, 0, 0.4), 0 0 20px ${skin.primaryColor}30`;
			label.style.color = '#fff';
			label.style.background = 'linear-gradient(to bottom, rgba(40, 40, 60, 0.8), rgba(30, 30, 45, 0.9))';
		});
		button.addEventListener('mouseout', () => {
			button.style.transform = 'scale(1)';
			button.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
			label.style.color = '#d0d0d0';
			label.style.background = 'linear-gradient(to bottom, rgba(30, 30, 45, 0.6), rgba(20, 20, 30, 0.8))';
		});

		return button;
	}

	/**
	 * Draw a mini worm preview on a canvas
	 */
	private drawMiniWorm(
		canvas: HTMLCanvasElement,
		skin: PatternSkin,
		animTime: number = 0
	): void {
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		const width = canvas.width;
		const height = canvas.height;
		const time = animTime / 1000;

		// Clear canvas with subtle gradient
		const bgGradient = ctx.createLinearGradient(0, 0, 0, height);
		bgGradient.addColorStop(0, 'rgba(25, 25, 40, 1)');
		bgGradient.addColorStop(1, 'rgba(15, 15, 25, 1)');
		ctx.fillStyle = bgGradient;
		ctx.fillRect(0, 0, width, height);

		// Draw mini worm segments - scaled to fit canvas
		const segmentCount = 7;
		const segmentRadius = 10;
		const segmentSpacing = 13;
		const startX = 16;
		const centerY = height / 2;

		for (let i = segmentCount - 1; i >= 0; i--) {
			const x = startX + i * segmentSpacing;
			// Wave animation for all patterns - makes UI feel lively
			const waveOffset = Math.sin(time * 2.5 + i * 0.4) * 2;
			const y = centerY + waveOffset;
			const t = i / (segmentCount - 1);

			// Get color for this segment with animation time, then apply Eye Shield+ muting
			const rawColor = this.getPatternSegmentColorForSkin(skin, i, segmentCount, t, animTime);
			const color = this.muteColor(rawColor);

			// Gradual size decrease toward tail (scaled for 7 segments)
			const radius = segmentRadius - i * 0.2;

			// Draw segment shadow
			ctx.beginPath();
			ctx.arc(x + 1, y + 2, radius, 0, Math.PI * 2);
			ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
			ctx.fill();

			// Draw segment
			ctx.beginPath();
			ctx.arc(x, y, radius, 0, Math.PI * 2);
			ctx.fillStyle = color;
			ctx.fill();

			// Add highlight
			ctx.beginPath();
			ctx.arc(x - 2, y - 2, radius * 0.25, 0, Math.PI * 2);
			ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
			ctx.fill();
		}

		// Draw mini eyes on head with wave offset
		const headX = startX;
		const headWave = Math.sin(time * 2.5) * 2;
		const headY = centerY + headWave;

		// Left eye (top)
		ctx.beginPath();
		ctx.arc(headX - 1, headY - 3.5, 2.5, 0, Math.PI * 2);
		ctx.fillStyle = '#ffffff';
		ctx.fill();
		ctx.beginPath();
		ctx.arc(headX - 1.5, headY - 3.5, 1.2, 0, Math.PI * 2);
		ctx.fillStyle = '#111111';
		ctx.fill();

		// Right eye (bottom)
		ctx.beginPath();
		ctx.arc(headX - 1, headY + 3.5, 2.5, 0, Math.PI * 2);
		ctx.fillStyle = '#ffffff';
		ctx.fill();
		ctx.beginPath();
		ctx.arc(headX - 1.5, headY + 3.5, 1.2, 0, Math.PI * 2);
		ctx.fillStyle = '#111111';
		ctx.fill();
	}

	/**
	 * Get segment color for a specific pattern skin (used for mini preview)
	 */
	private getPatternSegmentColorForSkin(
		skin: PatternSkin,
		segmentIndex: number,
		_totalSegments: number,
		t: number,
		animTime: number
	): string {
		const primary = skin.primaryColor;
		const secondary = skin.secondaryColor || '#ffffff';

		switch (skin.pattern) {
			case PatternType.STRIPED:
				return segmentIndex % 2 === 0 ? primary : secondary;

			case PatternType.SPOTTED:
				return segmentIndex % 3 === 0 ? secondary : primary;

			case PatternType.GRADIENT:
				return this.interpolateColor(primary, secondary, t);

			case PatternType.RAINBOW:
				// Animated rainbow color cycling - matches WormRenderer
				const rainbowShiftBtn = (t + animTime / 3000) % 1;
				return this.getRainbowColor(rainbowShiftBtn);

			case PatternType.CAMO:
				return segmentIndex % 2 === 0 ? primary : secondary;

			case PatternType.NEON:
				return this.applyNeonEffect(primary, t);

			case PatternType.METALLIC:
				return this.applyMetallicEffect(primary, secondary, t);

			case PatternType.ZIGZAG:
				// Alternating with varying width
				return (segmentIndex + Math.floor(segmentIndex / 2)) % 2 === 0 ? primary : secondary;

			case PatternType.CHECKER:
				// Alternating pattern
				return segmentIndex % 2 === 0 ? primary : secondary;

			case PatternType.TIGER:
				// Irregular stripes (thicker primary)
				return segmentIndex % 3 !== 2 ? primary : secondary;

			case PatternType.ZEBRA:
				// Regular alternating stripes
				return segmentIndex % 2 === 0 ? primary : secondary;

			case PatternType.ELECTRIC:
				// Bright core with electric effect - matches WormRenderer
				const electricPulseMini = Math.sin(animTime / 350 + segmentIndex) * 0.3 + 0.7;
				return segmentIndex % 3 === 0
					? secondary
					: this.applyBrightness(primary, electricPulseMini);

			case PatternType.GALAXY:
				// Dark with sparkles
				const sparkle = segmentIndex % 4 === 0 ? secondary : primary;
				return this.interpolateColor(primary, sparkle, 0.5 + t * 0.5);

			case PatternType.SUNSET:
				// Warm gradient
				return this.interpolateColor(primary, secondary, t);

			case PatternType.FROST:
				// Cool icy gradient with sparkle
				const frostBase = this.interpolateColor(primary, secondary, t);
				return segmentIndex % 3 === 0 ? '#ffffff' : frostBase;

			case PatternType.LAVA:
				// Hot flowing gradient - matches WormRenderer
				const lavaTMini = (t + Math.sin(animTime / 500 + segmentIndex * 0.5) * 0.2) % 1;
				return this.interpolateColor(primary, secondary, lavaTMini);

			case PatternType.TOXIC:
				// Glowing toxic with pulse - matches WormRenderer
				const toxicPulseMini = Math.sin(animTime / 400 + segmentIndex * 0.8) * 0.3 + 0.7;
				return this.applyBrightness(primary, toxicPulseMini);

			case PatternType.PLASMA:
				// Plasma flowing effect - matches WormRenderer
				const plasma1Mini = Math.sin(animTime / 500 + segmentIndex * 0.3);
				const plasma2Mini = Math.sin(animTime / 700 + segmentIndex * 0.5 + 2);
				const plasma3Mini = Math.sin(animTime / 400 + t * 6);
				const plasmaIntensityMini = (plasma1Mini + plasma2Mini + plasma3Mini) / 3 * 0.5 + 0.5;
				return this.interpolateColor(primary, secondary, plasmaIntensityMini);

			case PatternType.HOLOGRAPHIC:
				// Rainbow shimmer effect - matches WormRenderer
				const holoHueMini = (t * 360 + animTime / 20) % 360;
				const holoShimmerMini = Math.sin(animTime / 400 + segmentIndex * 0.8) * 0.3 + 0.7;
				return this.hslToHex(holoHueMini, 80, 60 * holoShimmerMini);

			case PatternType.VOID:
				// Dark pulsing with occasional bright flashes - matches WormRenderer
				const voidPulseMini = Math.sin(animTime / 800 + segmentIndex * 0.4) * 0.15 + 0.2;
				const voidSparkMini = Math.sin(animTime / 200 + segmentIndex * 3) > 0.95;
				return voidSparkMini ? '#aa44ff' : this.applyBrightness(primary, voidPulseMini);

			case PatternType.CYBER:
				// Digital scanning effect - matches WormRenderer
				const cyberFlowMini = Math.floor((animTime / 250 + segmentIndex) % 8);
				const cyberActiveMini = cyberFlowMini < 3;
				if (segmentIndex % 4 === 0) {
					return cyberActiveMini ? '#00ffff' : '#004444';
				}
				const cyberIntensityMini = cyberActiveMini ? 0.9 : 0.3;
				return this.applyBrightness(primary, cyberIntensityMini);

			case PatternType.CRYSTAL:
				// Sparkling crystal effect - matches WormRenderer
				const crystalFacetMini = (segmentIndex + Math.floor(animTime / 600)) % 5;
				const crystalShineMini = Math.sin(animTime / 500 + segmentIndex * 1.2) * 0.4 + 0.6;
				if (crystalFacetMini === 0) {
					return this.interpolateColor(primary, '#ffffff', crystalShineMini);
				}
				return this.interpolateColor(primary, secondary, crystalShineMini * 0.5);

			case PatternType.GLITCH:
				// Random glitching colors - matches WormRenderer
				const glitchSeedMini = Math.floor(animTime / 300);
				const glitchRandomMini = Math.sin(glitchSeedMini * segmentIndex * 12.9898) * 43758.5453 % 1;
				if (glitchRandomMini > 0.85) {
					const glitchColorsMini = ['#ff0000', '#00ff00', '#0000ff', '#ff00ff', '#00ffff'];
					return glitchColorsMini[Math.floor(glitchRandomMini * 5) % glitchColorsMini.length] ?? primary;
				}
				const glitchFlickerMini = Math.sin(animTime / 120 + segmentIndex) > 0.7 ? 1.2 : 0.8;
				return this.applyBrightness(primary, glitchFlickerMini);

			// ═══════════════════════════════════════════════════════════════════
			//  🕹️ RETRO ARCADE PATTERNS
			// ═══════════════════════════════════════════════════════════════════

			case PatternType.FIRE:
				// Flickering flame effect - oranges and yellows dancing
				const fireFlicker = Math.sin(animTime / 80 + segmentIndex * 1.2) * 0.5 + 0.5;
				const fireIntensity = Math.sin(animTime / 120 + segmentIndex * 0.8) * 0.3 + 0.7;
				return this.interpolateColor(primary, secondary, fireFlicker * fireIntensity);

			case PatternType.OCEAN:
				// Wave rippling effect - deep to light blue
				const wavePhase = Math.sin(animTime / 200 + segmentIndex * 0.5) * 0.5 + 0.5;
				const waveCrest = Math.sin(animTime / 150 + segmentIndex * 1.5) > 0.7;
				return waveCrest ? secondary : this.interpolateColor(primary, secondary, wavePhase);

			case PatternType.AURORA:
				// Northern lights flowing - greens and purples shifting
				const auroraShift = (t + animTime / 600) % 1;
				const auroraWave = Math.sin(animTime / 180 + segmentIndex * 0.7) * 0.4 + 0.5;
				return this.interpolateColor(primary, secondary, (auroraShift + auroraWave) % 1);

			case PatternType.DISCO:
				// Flashing disco ball segments - rapid color changes
				const discoPhase = Math.floor(animTime / 150) + segmentIndex;
				const discoBeat = (discoPhase % 4) / 4;
				const discoFlash = Math.sin(animTime / 100) > 0.5;
				if (discoFlash && segmentIndex % 2 === 0) {
					return '#ffffff';
				}
				return this.interpolateColor(primary, secondary, discoBeat);

			case PatternType.MATRIX:
				// Digital rain effect - cascading bright segments
				const matrixDrop = (animTime / 100 + segmentIndex * 2) % 6;
				const matrixBright = matrixDrop < 1.5 ? 1.4 : 0.6;
				const matrixGlow = segmentIndex % 3 === Math.floor(animTime / 200) % 3;
				return matrixGlow ? secondary : this.applyBrightness(primary, matrixBright);

			case PatternType.PULSE:
				// Heartbeat pulse waves - rhythmic brightness
				const pulsePhase = animTime / 400;
				const heartbeat = Math.pow(Math.sin(pulsePhase * Math.PI * 2), 2);
				const pulseWave = Math.max(0, Math.sin((t * 10 - pulsePhase * 4) * Math.PI));
				const pulseBright = 0.5 + heartbeat * 0.5 + pulseWave * 0.3;
				return this.applyBrightness(primary, pulseBright);

			case PatternType.SCALES:
				// Shimmering scales with light reflection
				const scaleShimmer = Math.sin(animTime / 150 + segmentIndex * 1.8) * 0.4 + 0.6;
				const isScaleHighlight = (segmentIndex + Math.floor(animTime / 300)) % 3 === 0;
				if (isScaleHighlight) {
					return this.interpolateColor(secondary, '#ffffff', scaleShimmer);
				}
				return this.interpolateColor(primary, secondary, t * scaleShimmer);

			case PatternType.LIGHTNING:
				// Random lightning bolt flashes
				const boltPhase = Math.floor(animTime / 80);
				const boltRandom = Math.sin(boltPhase * 43758.5453) % 1;
				const isLightningStrike = boltRandom > 0.85 && segmentIndex === Math.floor(boltRandom * 5);
				if (isLightningStrike) {
					return '#ffffff';
				}
				const stormDark = 0.4 + Math.sin(animTime / 200 + segmentIndex) * 0.2;
				return this.applyBrightness(primary, stormDark);

			case PatternType.CHROMATIC:
				// RGB chromatic aberration - shifting color channels
				const chromaOffset = (animTime / 300 + segmentIndex * 0.3) % 1;
				const rShift = Math.sin(chromaOffset * Math.PI * 2) * 0.5 + 0.5;
				const gShift = Math.sin((chromaOffset + 0.33) * Math.PI * 2) * 0.5 + 0.5;
				const bShift = Math.sin((chromaOffset + 0.66) * Math.PI * 2) * 0.5 + 0.5;
				const r = Math.floor(255 * rShift);
				const g = Math.floor(255 * gShift);
				const b = Math.floor(255 * bShift);
				return `rgb(${r}, ${g}, ${b})`;

			case PatternType.EMBER:
				// Glowing embers floating up effect
				const emberGlow = Math.sin(animTime / 100 + segmentIndex * 2.5) * 0.4 + 0.6;
				const emberPop = Math.sin(animTime / 60 + segmentIndex * 3) > 0.9;
				if (emberPop) {
					return secondary;
				}
				return this.applyBrightness(primary, emberGlow);

			case PatternType.HYPNOTIC:
				// Swirling hypnotic spiral effect
				const spiralPhase = (animTime / 250 + segmentIndex * 0.4) % 1;
				const spiralPulse = Math.sin(spiralPhase * Math.PI * 4) * 0.5 + 0.5;
				return segmentIndex % 2 === Math.floor(spiralPhase * 2) % 2
					? this.interpolateColor(primary, secondary, spiralPulse)
					: this.interpolateColor(secondary, primary, spiralPulse);

			case PatternType.PIXELATE:
				// Retro pixel art shifting - blocky color changes
				const pixelBlock = Math.floor(animTime / 200);
				const pixelPattern = (segmentIndex + pixelBlock) % 4;
				switch (pixelPattern) {
					case 0: return primary;
					case 1: return secondary;
					case 2: return this.interpolateColor(primary, secondary, 0.5);
					default: return primary;
				}

			// ═══════════════════════════════════════════════════════════════════
			//  🍯 NATURE & ORGANIC PATTERNS
			// ═══════════════════════════════════════════════════════════════════

			case PatternType.BEEHIVE:
				// Honeycomb shimmer effect - hexagonal cells glistening
				const honeyCellPhase = (segmentIndex + Math.floor(animTime / 150)) % 6;
				const honeyShimmer = Math.sin(animTime / 120 + segmentIndex * 1.2) * 0.3 + 0.7;
				const isHoneyHighlight = honeyCellPhase === 0;
				return isHoneyHighlight
					? this.interpolateColor(secondary, '#ffd700', honeyShimmer)
					: this.interpolateColor(primary, secondary, t * honeyShimmer);

			case PatternType.MARBLE:
				// Elegant marble veins flowing through stone
				const marbleVeinPhase = Math.sin(animTime / 300 + segmentIndex * 0.8 + t * 3);
				const marbleSwirl = Math.sin(animTime / 200 + segmentIndex * 1.5) * 0.2 + 0.5;
				const hasVein = marbleVeinPhase > 0.6;
				return hasVein
					? this.interpolateColor(secondary, primary, marbleSwirl)
					: this.applyBrightness(primary, 0.9 + marbleSwirl * 0.2);

			case PatternType.WOOD:
				// Natural wood grain rings - organic flow
				const woodRingPhase = (segmentIndex + Math.floor(animTime / 400)) % 5;
				const woodGrain = Math.sin(animTime / 250 + segmentIndex * 0.6) * 0.15 + 0.85;
				const isRing = woodRingPhase < 2;
				return isRing
					? this.applyBrightness(secondary, woodGrain)
					: this.applyBrightness(primary, woodGrain);

			case PatternType.CORAL:
				// Living coral reef texture - organic pulsing
				const coralPulse = Math.sin(animTime / 180 + segmentIndex * 1.3) * 0.3 + 0.7;
				const coralBranch = Math.sin(animTime / 250 + segmentIndex * 2) > 0.5;
				const coralGlow = Math.sin(animTime / 100 + segmentIndex) * 0.2 + 0.8;
				return coralBranch
					? this.applyBrightness(secondary, coralPulse * coralGlow)
					: this.applyBrightness(primary, coralPulse);

			case PatternType.FEATHER:
				// Iridescent feather pattern - rainbow shift on angle
				const featherIridescence = (t + animTime / 400 + segmentIndex * 0.1) % 1;
				const featherSheen = Math.sin(animTime / 150 + segmentIndex * 2) * 0.4 + 0.6;
				const featherBlend = Math.sin(featherIridescence * Math.PI * 2) * 0.5 + 0.5;
				return this.interpolateColor(primary, secondary, featherBlend * featherSheen);

			// ═══════════════════════════════════════════════════════════════════
			//  🔌 TECH & FUTURISTIC PATTERNS
			// ═══════════════════════════════════════════════════════════════════

			case PatternType.CIRCUIT:
				// Electronic circuit board traces - data flowing
				const circuitTrace = (animTime / 80 + segmentIndex * 2) % 8;
				const circuitFlow = circuitTrace < 2 ? 1.5 : 0.7;
				const isTraceNode = segmentIndex % 4 === Math.floor(animTime / 300) % 4;
				if (isTraceNode) {
					return secondary;
				}
				return this.applyBrightness(primary, circuitFlow);

			case PatternType.HOLOGRAM:
				// 3D hologram scan lines - flickering projection
				const holoScanLine = (animTime / 60 + segmentIndex) % 4;
				const holoFlicker = Math.sin(animTime / 40) * 0.3 + 0.7;
				const holoRGB = Math.sin(animTime / 100 + segmentIndex * 0.5) * 0.5 + 0.5;
				if (holoScanLine < 1) {
					return this.interpolateColor(secondary, '#ffffff', holoFlicker);
				}
				return this.interpolateColor(primary, secondary, holoRGB * holoFlicker);

			case PatternType.QUANTUM:
				// Quantum particle uncertainty - probabilistic shimmer
				const quantumPhase = Math.sin(animTime / 50 + segmentIndex * 4.5) * 0.5 + 0.5;
				const quantumUncertainty = Math.random() * 0.3; // Deliberate randomness
				const quantumGlow = 0.4 + quantumPhase * 0.6 + quantumUncertainty;
				const quantumPop = Math.sin(animTime / 80 + segmentIndex * 3) > 0.95;
				if (quantumPop) {
					return secondary;
				}
				return this.applyBrightness(primary, quantumGlow);

			case PatternType.BINARY:
				// Binary code streaming - 1s and 0s cascading
				const binaryStream = Math.floor(animTime / 100 + segmentIndex * 1.5) % 2;
				const binaryGlow = Math.sin(animTime / 80 + segmentIndex * 2) * 0.4 + 0.6;
				const binaryBright = binaryStream === 1 ? 1.3 : 0.5;
				return this.applyBrightness(binaryStream === 1 ? secondary : primary, binaryGlow * binaryBright);

			// ═══════════════════════════════════════════════════════════════════
			//  🎨 ARTISTIC PATTERNS
			// ═══════════════════════════════════════════════════════════════════

			case PatternType.MOSAIC:
				// Stained glass mosaic tiles - shifting colored fragments
				const mosaicTile = (segmentIndex + Math.floor(animTime / 200)) % 5;
				const mosaicGlow = Math.sin(animTime / 150 + segmentIndex * 1.2) * 0.25 + 0.75;
				const mosaicLight = Math.sin(animTime / 100 + mosaicTile) > 0.7;
				if (mosaicLight) {
					return this.interpolateColor(secondary, '#ffffff', mosaicGlow * 0.5);
				}
				return mosaicTile % 2 === 0
					? this.applyBrightness(primary, mosaicGlow)
					: this.applyBrightness(secondary, mosaicGlow);

			case PatternType.WATERCOLOR:
				// Soft watercolor blending - organic color bleeding
				const waterBlend = Math.sin(animTime / 250 + segmentIndex * 0.8) * 0.5 + 0.5;
				const waterFlow = Math.sin(animTime / 180 + t * 4) * 0.3 + 0.5;
				const waterSoft = (waterBlend + waterFlow) / 2;
				return this.interpolateColor(primary, secondary, waterSoft);

			case PatternType.TRIBAL:
				// Bold tribal tattoo markings - rhythmic patterns
				const tribalBeat = Math.sin(animTime / 200 + segmentIndex * 1.5) * 0.3 + 0.7;
				const tribalMark = segmentIndex % 3;
				const tribalPulse = Math.sin(animTime / 150) > 0.5;
				if (tribalMark === 0) {
					return this.applyBrightness(secondary, tribalBeat);
				}
				return tribalPulse && tribalMark === 1
					? this.interpolateColor(primary, secondary, 0.5)
					: this.applyBrightness(primary, tribalBeat);

			case PatternType.ORIGAMI:
				// Paper fold geometric patterns - angular light reflections
				const origamiFold = (segmentIndex + Math.floor(animTime / 180)) % 4;
				const origamiLight = Math.sin(animTime / 120 + segmentIndex * 2) * 0.4 + 0.6;
				const origamiCrease = origamiFold < 2;
				if (origamiCrease) {
					return this.interpolateColor(primary, '#ffffff', origamiLight * 0.3);
				}
				return this.interpolateColor(primary, secondary, origamiLight);

			default:
				return this.applyGradientDarkening(primary, t);
		}
	}

	/**
	 * Apply brightness to a color
	 */
	private applyBrightness(hexColor: string, factor: number): string {
		const r = parseInt(hexColor.slice(1, 3), 16);
		const g = parseInt(hexColor.slice(3, 5), 16);
		const b = parseInt(hexColor.slice(5, 7), 16);

		const newR = Math.min(255, Math.floor(r * factor));
		const newG = Math.min(255, Math.floor(g * factor));
		const newB = Math.min(255, Math.floor(b * factor));

		return `rgb(${newR}, ${newG}, ${newB})`;
	}

	/**
	 * Create a flag skin button with mini worm preview
	 */
	private createFlagButton(skin: FlagSkin, onClick: () => void): HTMLButtonElement {
		const button = document.createElement('button');
		button.className = 'skin-btn';
		button.dataset.skinId = skin.id;
		button.title = skin.country;

		// Create mini canvas for worm preview (responsive sizing handled by CSS)
		const canvas = document.createElement('canvas');
		canvas.width = 110;
		canvas.height = 58;
		button.appendChild(canvas);

		// Draw mini worm on canvas
		this.drawMiniFlagWorm(canvas, skin, 0);

		// Register canvas for wave animation
		const ctx = canvas.getContext('2d');
		if (ctx) {
			this.flagButtonCanvases.set(canvas, { skin, ctx });
		}

		// Add label with CSS class
		const label = document.createElement('div');
		label.className = 'skin-label';
		label.textContent = skin.name;
		button.appendChild(label);

		const primaryFlagColor = skin.colors[0] || '#ffffff';
		button.addEventListener('click', onClick);
		button.addEventListener('mouseover', () => {
			button.style.transform = 'scale(1.06) translateY(-2px)';
			button.style.boxShadow = `0 4px 16px rgba(0, 0, 0, 0.4), 0 0 20px ${primaryFlagColor}30`;
			label.style.color = '#fff';
			label.style.background = 'linear-gradient(to bottom, rgba(40, 40, 60, 0.8), rgba(30, 30, 45, 0.9))';
		});
		button.addEventListener('mouseout', () => {
			button.style.transform = 'scale(1)';
			button.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
			label.style.color = '#d0d0d0';
			label.style.background = 'linear-gradient(to bottom, rgba(30, 30, 45, 0.6), rgba(20, 20, 30, 0.8))';
		});

		return button;
	}

	/**
	 * Draw a mini flag worm preview on canvas with wave animation
	 */
	private drawMiniFlagWorm(canvas: HTMLCanvasElement, skin: FlagSkin, animTime: number = 0): void {
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		const width = canvas.width;
		const height = canvas.height;
		const time = animTime / 1000;

		// Clear canvas with subtle gradient
		const bgGradient = ctx.createLinearGradient(0, 0, 0, height);
		bgGradient.addColorStop(0, 'rgba(25, 25, 40, 1)');
		bgGradient.addColorStop(1, 'rgba(15, 15, 25, 1)');
		ctx.fillStyle = bgGradient;
		ctx.fillRect(0, 0, width, height);

		// Draw mini worm segments with flag colors - scaled for new canvas size
		const segmentCount = 7;
		const segmentRadius = 10;
		const segmentSpacing = 13;
		const startX = 16;
		const centerY = height / 2;
		const colors = skin.colors;

		for (let i = segmentCount - 1; i >= 0; i--) {
			const x = startX + i * segmentSpacing;
			// Wave animation for lively feel
			const waveOffset = Math.sin(time * 2.5 + i * 0.4) * 2;
			const y = centerY + waveOffset;

			// Get flag color for this segment, then apply Eye Shield+ muting
			const colorIndex = Math.floor((i / segmentCount) * colors.length);
			const rawColor = colors[Math.min(colorIndex, colors.length - 1)] || '#ffffff';
			const color = this.muteColor(rawColor);

			// Draw segment shadow
			ctx.beginPath();
			ctx.arc(x + 1, y + 2, segmentRadius - i * 0.25, 0, Math.PI * 2);
			ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
			ctx.fill();

			// Draw segment
			ctx.beginPath();
			ctx.arc(x, y, segmentRadius - i * 0.25, 0, Math.PI * 2);
			ctx.fillStyle = color;
			ctx.fill();

			// Add highlight
			ctx.beginPath();
			ctx.arc(x - 2, y - 2, segmentRadius * 0.25, 0, Math.PI * 2);
			ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
			ctx.fill();
		}

		// Draw mini eyes on head with wave offset
		const headX = startX;
		const headWave = Math.sin(time * 2.5) * 2;
		const headY = centerY + headWave;

		// Eyes
		ctx.beginPath();
		ctx.arc(headX - 1, headY - 3.5, 3, 0, Math.PI * 2);
		ctx.fillStyle = '#ffffff';
		ctx.fill();
		ctx.beginPath();
		ctx.arc(headX - 1.5, headY - 3.5, 1.5, 0, Math.PI * 2);
		ctx.fillStyle = '#111111';
		ctx.fill();

		ctx.beginPath();
		ctx.arc(headX - 1, headY + 3.5, 3, 0, Math.PI * 2);
		ctx.fillStyle = '#ffffff';
		ctx.fill();
		ctx.beginPath();
		ctx.arc(headX - 1.5, headY + 3.5, 1.5, 0, Math.PI * 2);
		ctx.fillStyle = '#111111';
		ctx.fill();
	}

	/**
	 * Switch to a different tab (Colors, Patterns, Flags)
	 */
	switchTab(tab: SkinTab): void {
		this.activeTab = tab;

		// Tab colors mapping
		const tabColors: Record<SkinTab, string> = {
			[SkinTab.COLORS]: '#ff6b6b',
			[SkinTab.PATTERNS]: '#ffd93d',
			[SkinTab.FLAGS]: '#6bcb77',
		};

		// Update tab button styles
		this.tabButtons.forEach((button, tabId) => {
			if (tabId === tab) {
				const color = tabColors[tabId];
				button.style.background = `linear-gradient(135deg, ${color}, ${color}cc)`;
				button.style.color = '#fff';
				button.style.boxShadow = `0 4px 15px ${color}40`;
				button.style.transform = 'translateY(0)';
				button.style.textShadow = '0 1px 2px rgba(0, 0, 0, 0.3)';
			} else {
				button.style.background = 'transparent';
				button.style.color = '#888';
				button.style.boxShadow = 'none';
				button.style.transform = 'translateY(0)';
			}
		});

		// Show/hide panels
		this.tabPanels.forEach((panel, tabId) => {
			panel.style.display = tabId === tab ? 'block' : 'none';
		});
	}

	/**
	 * Select a skin
	 */
	private selectSkin(skin: PlayerSkin): void {
		this.selectedSkin = skin;
		saveSkinToStorage(skin);
		this.selectSkinInUI(skin);
		this.events.onSkinChange(skin);
	}

	/**
	 * Update UI to show selected skin
	 */
	private selectSkinInUI(skin: PlayerSkin): void {
		// Clear all selections
		this.tabPanels.forEach((panel) => {
			const buttons = panel.querySelectorAll('button');
			buttons.forEach((button) => {
				const btn = button as HTMLElement;
				btn.style.borderColor = 'transparent';
				btn.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
			});
		});

		// Find and highlight the selected skin button
		this.tabPanels.forEach((panel) => {
			const buttons = panel.querySelectorAll('button');
			buttons.forEach((button) => {
				const btn = button as HTMLElement;
				if (btn.dataset.skinId === skin.id) {
					btn.style.borderColor = '#4ecdc4';
					btn.style.boxShadow = '0 2px 12px rgba(78, 205, 196, 0.5), 0 0 20px rgba(78, 205, 196, 0.3)';
				}
			});
		});

		// Switch to the appropriate tab based on skin type
		switch (skin.type) {
			case SkinType.COLOR:
				this.switchTab(SkinTab.COLORS);
				break;
			case SkinType.PATTERN:
				this.switchTab(SkinTab.PATTERNS);
				break;
			case SkinType.FLAG:
				this.switchTab(SkinTab.FLAGS);
				break;
		}
	}

	/**
	 * Start the preview animation
	 */
	private startPreviewAnimation(): void {
		// Prevent multiple animation loops
		if (this.animationFrame) return;

		const animate = () => {
			// Re-check eye protection setting every frame (in case it changed)
			this._eyeProtectionEnabled = this.checkEyeProtection();

			// Check if animations are disabled via EyeProtectionSettings
			const animationsDisabled = !EyeProtectionSettings.getInstance().areAnimationsEnabled();

			if (animationsDisabled) {
				// Animations completely disabled - freeze at current frame
				this.drawPreview();
				// Don't increment animationTime, don't update buttons
			} else if (this._eyeProtectionEnabled) {
				// Eye protection: very slow animation, no button updates
				this.animationTime += 0.5; // 32x slower - almost static
				this.drawPreview();
				// Don't update animated buttons at all in eye protection mode
			} else {
				// Normal mode: slower, gentler animations (was 16, now 8 for smoother feel)
				this.animationTime += 8;
				this.drawPreview();

				// Update animated button canvases (throttled to every ~150ms for calmer effect)
				if (this.animationTime - this.lastButtonUpdate > 150) {
					this.lastButtonUpdate = this.animationTime;
					this.updateAnimatedButtons();
				}
			}

			this.animationFrame = requestAnimationFrame(animate);
		};
		this.animationFrame = requestAnimationFrame(animate);
	}

	/**
	 * Set eye protection mode (call when setting changes)
	 */
	setEyeProtection(enabled: boolean): void {
		this._eyeProtectionEnabled = enabled;
		// Redraw static preview if enabling eye protection
		if (enabled) {
			this.drawPreview();
		}
	}

	/**
	 * Set animations enabled state (call when setting changes)
	 */
	setAnimationsEnabled(enabled: boolean): void {
		// Animations are now controlled by EyeProtectionSettings
		// If disabling animations, set to MILD; if enabling, set to OFF
		const settings = EyeProtectionSettings.getInstance();
		if (enabled && !settings.areAnimationsEnabled()) {
			settings.setLevel(0); // OFF - enables animations
		} else if (!enabled && settings.areAnimationsEnabled()) {
			settings.setLevel(1); // MILD - disables animations
		}
		// Redraw preview with current state
		this.drawPreview();
	}

	/**
	 * Stop the preview animation (call when hiding UI to save resources)
	 */
	stopAnimation(): void {
		if (this.animationFrame) {
			cancelAnimationFrame(this.animationFrame);
			this.animationFrame = null;
		}
	}

	/**
	 * Start/resume the preview animation (call when showing UI)
	 */
	startAnimation(): void {
		this.startPreviewAnimation();
	}

	/**
	 * Update all button canvases with wave animation
	 */
	private updateAnimatedButtons(): void {
		// Update pattern button canvases
		this.animatedButtonCanvases.forEach(({ skin }, canvas) => {
			this.drawMiniWorm(canvas, skin, this.animationTime);
		});

		// Update color button canvases
		this.colorButtonCanvases.forEach(({ skin }, canvas) => {
			this.drawMiniColorWorm(canvas, skin, this.animationTime);
		});

		// Update flag button canvases
		this.flagButtonCanvases.forEach(({ skin }, canvas) => {
			this.drawMiniFlagWorm(canvas, skin, this.animationTime);
		});
	}

	/**
	 * Draw the worm preview - large, impressive hero display
	 */
	private drawPreview(): void {
		const ctx = this.previewCtx;
		if (!ctx) return;

		const canvas = this.previewCanvas;
		const width = canvas.width;
		const height = canvas.height;

		// Clear canvas with atmospheric gradient
		const bgGradient = ctx.createLinearGradient(0, 0, 0, height);
		bgGradient.addColorStop(0, 'rgba(20, 20, 38, 1)');
		bgGradient.addColorStop(0.5, 'rgba(15, 15, 30, 1)');
		bgGradient.addColorStop(1, 'rgba(12, 12, 24, 1)');
		ctx.fillStyle = bgGradient;
		ctx.fillRect(0, 0, width, height);

		// Add subtle ambient particles/dots for atmosphere
		ctx.globalAlpha = 0.15;
		const time = this.animationTime / 1000;
		for (let i = 0; i < 20; i++) {
			const px = ((i * 73 + time * 10) % width);
			const py = ((i * 47 + Math.sin(time + i) * 20) % height);
			const size = 1 + Math.sin(time * 2 + i) * 0.5;
			ctx.beginPath();
			ctx.arc(px, py, size, 0, Math.PI * 2);
			ctx.fillStyle = '#4ecdc4';
			ctx.fill();
		}
		ctx.globalAlpha = 1;

		// Draw large impressive worm - many segments for visual impact
		const segmentCount = 14;
		const segmentRadius = 32;
		const segmentSpacing = 40;
		const startX = 100;
		const centerY = height / 2;

		// Calculate fluid wave animation
		for (let i = segmentCount - 1; i >= 0; i--) {
			const x = startX + i * segmentSpacing;
			// More dramatic serpentine movement with varying amplitude
			const waveAmp = 22 + Math.sin(time * 0.5) * 5;
			const waveOffset = Math.sin(time * 3.0 + i * 0.45) * waveAmp;
			const y = centerY + waveOffset;
			// Gradual size decrease toward tail
			const radius = segmentRadius - i * 1.2;

			// Get color for this segment based on skin
			const color = this.getSegmentColor(i, segmentCount);

			// Draw segment glow (outer aura)
			ctx.beginPath();
			ctx.arc(x, y, radius + 4, 0, Math.PI * 2);
			ctx.fillStyle = this.addAlpha(color, 0.15);
			ctx.fill();

			// Draw segment shadow
			ctx.beginPath();
			ctx.arc(x + 3, y + 4, radius, 0, Math.PI * 2);
			ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
			ctx.fill();

			// Draw main segment
			ctx.beginPath();
			ctx.arc(x, y, radius, 0, Math.PI * 2);
			ctx.fillStyle = color;
			ctx.fill();

			// Add inner highlight
			ctx.beginPath();
			ctx.arc(x - radius * 0.2, y - radius * 0.2, radius * 0.4, 0, Math.PI * 2);
			ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
			ctx.fill();

			// Add specular highlight
			ctx.beginPath();
			ctx.arc(x - radius * 0.3, y - radius * 0.3, radius * 0.15, 0, Math.PI * 2);
			ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
			ctx.fill();
		}

		// Draw expressive eyes on head
		const headX = startX;
		const headWaveAmp = 22 + Math.sin(time * 0.5) * 5;
		const headWave = Math.sin(time * 3.0) * headWaveAmp;
		const headY = centerY + headWave;
		const eyeRadius = 8;
		const pupilRadius = 4;

		// Eye positions (perpendicular to movement direction)
		const eyeOffsetX = -4;
		const eyeOffsetY = 10;

		// Left eye (top)
		ctx.beginPath();
		ctx.arc(headX + eyeOffsetX, headY - eyeOffsetY, eyeRadius, 0, Math.PI * 2);
		ctx.fillStyle = '#ffffff';
		ctx.fill();
		// Add eye shadow
		ctx.beginPath();
		ctx.arc(headX + eyeOffsetX, headY - eyeOffsetY, eyeRadius, 0, Math.PI * 2);
		ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
		ctx.lineWidth = 1;
		ctx.stroke();
		// Pupil (looking forward-left)
		ctx.beginPath();
		ctx.arc(headX + eyeOffsetX - 2, headY - eyeOffsetY, pupilRadius, 0, Math.PI * 2);
		ctx.fillStyle = '#111111';
		ctx.fill();
		// Eye highlight
		ctx.beginPath();
		ctx.arc(headX + eyeOffsetX - 1, headY - eyeOffsetY - 2, 2, 0, Math.PI * 2);
		ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
		ctx.fill();

		// Right eye (bottom)
		ctx.beginPath();
		ctx.arc(headX + eyeOffsetX, headY + eyeOffsetY, eyeRadius, 0, Math.PI * 2);
		ctx.fillStyle = '#ffffff';
		ctx.fill();
		ctx.beginPath();
		ctx.arc(headX + eyeOffsetX, headY + eyeOffsetY, eyeRadius, 0, Math.PI * 2);
		ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
		ctx.lineWidth = 1;
		ctx.stroke();
		// Pupil
		ctx.beginPath();
		ctx.arc(headX + eyeOffsetX - 2, headY + eyeOffsetY, pupilRadius, 0, Math.PI * 2);
		ctx.fillStyle = '#111111';
		ctx.fill();
		// Eye highlight
		ctx.beginPath();
		ctx.arc(headX + eyeOffsetX - 1, headY + eyeOffsetY - 2, 2, 0, Math.PI * 2);
		ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
		ctx.fill();

		// Draw accessory on head
		this.drawAccessory(ctx, headX, headY, segmentRadius);
	}

	/**
	 * Draw the selected accessory on the preview worm
	 * Uses the shared accessory drawing system for consistency with in-game rendering
	 */
	private drawAccessory(ctx: CanvasRenderingContext2D, headX: number, headY: number, radius: number): void {
		if (this.selectedAccessory === 'none') return;

		ctx.save();
		ctx.translate(headX, headY);
		// The preview worm faces LEFT (simulated headRotation = π)
		// Use the same formula as in-game: headRotation + categoryOffset
		// This ensures accessories appear in the same relative position as in-game
		const accessoryType = this.selectedAccessory as AccessoryType;
		const categoryPosition = getAccessoryPosition(accessoryType);
		const previewHeadRotation = Math.PI; // Preview worm faces left
		ctx.rotate(previewHeadRotation + categoryPosition.rotationOffset);

		// Use the shared accessory drawing system
		const adapter = new Canvas2DAdapter(ctx);
		drawSharedAccessory(adapter, accessoryType, radius);

		ctx.restore();
	}

	// NOTE: All individual accessory drawing methods have been removed and consolidated
	// into the shared system at @wooorms/shared/accessories.ts
	// This ensures consistency between preview and in-game rendering.

	/**
	 * Add alpha to a color string
	 */
	private addAlpha(color: string, alpha: number): string {
		// Handle rgb format
		if (color.startsWith('rgb(')) {
			return color.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
		}
		// Handle hsl format
		if (color.startsWith('hsl(')) {
			return color.replace('hsl(', 'hsla(').replace(')', `, ${alpha})`);
		}
		// Handle hex format - convert to rgba
		if (color.startsWith('#')) {
			const r = parseInt(color.slice(1, 3), 16);
			const g = parseInt(color.slice(3, 5), 16);
			const b = parseInt(color.slice(5, 7), 16);
			return `rgba(${r}, ${g}, ${b}, ${alpha})`;
		}
		return color;
	}

	/**
	 * Get segment color based on selected skin (with Eye Shield+ muting applied)
	 */
	private getSegmentColor(segmentIndex: number, totalSegments: number): string {
		const skin = this.selectedSkin;
		const t = segmentIndex / (totalSegments - 1); // 0 = head, 1 = tail

		let color: string;
		switch (skin.type) {
			case SkinType.COLOR:
				color = this.applyGradientDarkening(skin.primaryColor, t);
				break;

			case SkinType.PATTERN:
				color = this.getPatternSegmentColor(skin, segmentIndex, t);
				break;

			case SkinType.FLAG:
				color = this.getFlagSegmentColor(skin, segmentIndex, totalSegments);
				break;

			default:
				color = '#4ecdc4';
		}

		// Apply Eye Shield+ color muting if enabled
		return this.muteColor(color);
	}

	/**
	 * Apply gradient darkening from head to tail
	 */
	private applyGradientDarkening(hexColor: string, t: number): string {
		const r = parseInt(hexColor.slice(1, 3), 16);
		const g = parseInt(hexColor.slice(3, 5), 16);
		const b = parseInt(hexColor.slice(5, 7), 16);

		// Darken towards tail
		const factor = 1 - t * 0.4;
		const newR = Math.floor(r * factor);
		const newG = Math.floor(g * factor);
		const newB = Math.floor(b * factor);

		return `rgb(${newR}, ${newG}, ${newB})`;
	}

	/**
	 * Get color for pattern skin segment
	 */
	private getPatternSegmentColor(skin: PatternSkin, segmentIndex: number, t: number): string {
		const primary = skin.primaryColor;
		const secondary = skin.secondaryColor || '#ffffff';

		switch (skin.pattern) {
			case PatternType.SOLID:
				return this.applyGradientDarkening(primary, t);

			case PatternType.STRIPED:
				return segmentIndex % 2 === 0 ? primary : secondary;

			case PatternType.SPOTTED:
				return segmentIndex % 3 === 0 ? secondary : primary;

			case PatternType.GRADIENT:
				return this.interpolateColor(primary, secondary, t);

			case PatternType.RAINBOW:
				// Animated rainbow color cycling - matches WormRenderer timing
				const rainbowShiftPrev = (t + this.animationTime / 3000) % 1;
				return this.getRainbowColor(rainbowShiftPrev);

			case PatternType.CAMO:
				return segmentIndex % 2 === 0 ? primary : secondary;

			case PatternType.NEON:
				return this.applyNeonEffect(primary, t);

			case PatternType.METALLIC:
				return this.applyMetallicEffect(primary, secondary, t);

			case PatternType.ZIGZAG:
				// Irregular alternating pattern
				return (segmentIndex + Math.floor(segmentIndex / 2)) % 2 === 0 ? primary : secondary;

			case PatternType.CHECKER:
				// Simple alternating
				return segmentIndex % 2 === 0 ? primary : secondary;

			case PatternType.TIGER:
				// Thick primary with thin secondary stripes
				return segmentIndex % 3 !== 2 ? primary : secondary;

			case PatternType.ZEBRA:
				// Classic alternating stripes
				return segmentIndex % 2 === 0 ? primary : secondary;

			case PatternType.ELECTRIC:
				// Electric pulsing effect - matches WormRenderer timing
				const electricPulse = Math.sin(this.animationTime / 350 + segmentIndex) * 0.3 + 0.7;
				return segmentIndex % 3 === 0
					? secondary
					: this.applyBrightness(primary, electricPulse);

			case PatternType.GALAXY:
				// Dark base with bright sparkles
				const sparkle = segmentIndex % 4 === 0 ? secondary : primary;
				return this.interpolateColor(primary, sparkle, 0.5 + t * 0.5);

			case PatternType.SUNSET:
				// Warm color gradient
				return this.interpolateColor(primary, secondary, t);

			case PatternType.FROST:
				// Cool icy gradient with white sparkles
				const frostBase = this.interpolateColor(primary, secondary, t);
				return segmentIndex % 3 === 0 ? '#ffffff' : frostBase;

			case PatternType.LAVA:
				// Hot flowing gradient with movement - matches WormRenderer timing
				const lavaT = Math.abs((t + Math.sin(this.animationTime / 500 + segmentIndex * 0.5) * 0.2) % 1);
				return this.interpolateColor(primary, secondary, lavaT);

			case PatternType.TOXIC:
				// Glowing toxic pulse - matches WormRenderer timing
				const toxicPulse = Math.sin(this.animationTime / 400 + segmentIndex * 0.8) * 0.3 + 0.7;
				return this.applyBrightness(primary, toxicPulse);

			case PatternType.PLASMA:
				// Plasma flowing effect - matches WormRenderer timing
				const plasma1 = Math.sin(this.animationTime / 500 + segmentIndex * 0.3);
				const plasma2 = Math.sin(this.animationTime / 700 + segmentIndex * 0.5 + 2);
				const plasma3 = Math.sin(this.animationTime / 400 + t * 6);
				const plasmaIntensity = (plasma1 + plasma2 + plasma3) / 3 * 0.5 + 0.5;
				return this.interpolateColor(primary, secondary, plasmaIntensity);

			case PatternType.HOLOGRAPHIC:
				// Rainbow shimmer effect - matches WormRenderer timing
				const holoHue = (t * 360 + this.animationTime / 20) % 360;
				const holoShimmer = Math.sin(this.animationTime / 400 + segmentIndex * 0.8) * 0.3 + 0.7;
				return this.hslToHex(holoHue, 80, 60 * holoShimmer);

			case PatternType.VOID:
				// Dark pulsing with occasional bright flashes - matches WormRenderer timing
				const voidPulse = Math.sin(this.animationTime / 800 + segmentIndex * 0.4) * 0.15 + 0.2;
				const voidSpark = Math.sin(this.animationTime / 200 + segmentIndex * 3) > 0.95;
				return voidSpark ? '#aa44ff' : this.applyBrightness(primary, voidPulse);

			case PatternType.CYBER:
				// Digital scanning effect - matches WormRenderer timing
				const cyberFlow = Math.floor((this.animationTime / 250 + segmentIndex) % 8);
				const cyberActive = cyberFlow < 3;
				if (segmentIndex % 4 === 0) {
					return cyberActive ? '#00ffff' : '#004444';
				}
				const cyberIntensity = cyberActive ? 0.9 : 0.3;
				return this.applyBrightness(primary, cyberIntensity);

			case PatternType.CRYSTAL:
				// Sparkling crystal effect - matches WormRenderer timing
				const crystalFacet = (segmentIndex + Math.floor(this.animationTime / 600)) % 5;
				const crystalShine = Math.sin(this.animationTime / 500 + segmentIndex * 1.2) * 0.4 + 0.6;
				if (crystalFacet === 0) {
					return this.interpolateColor(primary, '#ffffff', crystalShine);
				}
				return this.interpolateColor(primary, secondary, crystalShine * 0.5);

			case PatternType.GLITCH:
				// Random glitching colors - matches WormRenderer timing
				const glitchSeed = Math.floor(this.animationTime / 300);
				const glitchRandom = Math.sin(glitchSeed * segmentIndex * 12.9898) * 43758.5453 % 1;
				if (glitchRandom > 0.85) {
					const glitchColors = ['#ff0000', '#00ff00', '#0000ff', '#ff00ff', '#00ffff'];
					return glitchColors[Math.floor(glitchRandom * 5) % glitchColors.length] ?? primary;
				}
				const glitchFlicker = Math.sin(this.animationTime / 120 + segmentIndex) > 0.7 ? 1.2 : 0.8;
				return this.applyBrightness(primary, glitchFlicker);

			// ═══════════════════════════════════════════════════════════════════
			//  🕹️ RETRO ARCADE PATTERNS
			// ═══════════════════════════════════════════════════════════════════

			case PatternType.FIRE:
				// Flickering flame effect - oranges and yellows dancing
				const fireFlickerPrev = Math.sin(this.animationTime / 80 + segmentIndex * 1.2) * 0.5 + 0.5;
				const fireIntensityPrev = Math.sin(this.animationTime / 120 + segmentIndex * 0.8) * 0.3 + 0.7;
				return this.interpolateColor(primary, secondary, fireFlickerPrev * fireIntensityPrev);

			case PatternType.OCEAN:
				// Wave rippling effect - deep to light blue
				const wavePhasePrev = Math.sin(this.animationTime / 200 + segmentIndex * 0.5) * 0.5 + 0.5;
				const waveCrestPrev = Math.sin(this.animationTime / 150 + segmentIndex * 1.5) > 0.7;
				return waveCrestPrev ? secondary : this.interpolateColor(primary, secondary, wavePhasePrev);

			case PatternType.AURORA:
				// Northern lights flowing - greens and purples shifting
				const auroraShiftPrev = (t + this.animationTime / 600) % 1;
				const auroraWavePrev = Math.sin(this.animationTime / 180 + segmentIndex * 0.7) * 0.4 + 0.5;
				return this.interpolateColor(primary, secondary, (auroraShiftPrev + auroraWavePrev) % 1);

			case PatternType.DISCO:
				// Flashing disco ball segments - rapid color changes
				const discoPhasePrev = Math.floor(this.animationTime / 150) + segmentIndex;
				const discoBeatPrev = (discoPhasePrev % 4) / 4;
				const discoFlashPrev = Math.sin(this.animationTime / 100) > 0.5;
				if (discoFlashPrev && segmentIndex % 2 === 0) {
					return '#ffffff';
				}
				return this.interpolateColor(primary, secondary, discoBeatPrev);

			case PatternType.MATRIX:
				// Digital rain effect - cascading bright segments
				const matrixDropPrev = (this.animationTime / 100 + segmentIndex * 2) % 6;
				const matrixBrightPrev = matrixDropPrev < 1.5 ? 1.4 : 0.6;
				const matrixGlowPrev = segmentIndex % 3 === Math.floor(this.animationTime / 200) % 3;
				return matrixGlowPrev ? secondary : this.applyBrightness(primary, matrixBrightPrev);

			case PatternType.PULSE:
				// Heartbeat pulse waves - rhythmic brightness
				const pulsePhasePrev = this.animationTime / 400;
				const heartbeatPrev = Math.pow(Math.sin(pulsePhasePrev * Math.PI * 2), 2);
				const pulseWavePrev = Math.max(0, Math.sin((t * 10 - pulsePhasePrev * 4) * Math.PI));
				const pulseBrightPrev = 0.5 + heartbeatPrev * 0.5 + pulseWavePrev * 0.3;
				return this.applyBrightness(primary, pulseBrightPrev);

			case PatternType.SCALES:
				// Shimmering scales with light reflection
				const scaleShimmerPrev = Math.sin(this.animationTime / 150 + segmentIndex * 1.8) * 0.4 + 0.6;
				const isScaleHighlightPrev = (segmentIndex + Math.floor(this.animationTime / 300)) % 3 === 0;
				if (isScaleHighlightPrev) {
					return this.interpolateColor(secondary, '#ffffff', scaleShimmerPrev);
				}
				return this.interpolateColor(primary, secondary, t * scaleShimmerPrev);

			case PatternType.LIGHTNING:
				// Random lightning bolt flashes
				const boltPhasePrev = Math.floor(this.animationTime / 80);
				const boltRandomPrev = Math.sin(boltPhasePrev * 43758.5453) % 1;
				const isLightningStrikePrev = boltRandomPrev > 0.85 && segmentIndex === Math.floor(boltRandomPrev * 12);
				if (isLightningStrikePrev) {
					return '#ffffff';
				}
				const stormDarkPrev = 0.4 + Math.sin(this.animationTime / 200 + segmentIndex) * 0.2;
				return this.applyBrightness(primary, stormDarkPrev);

			case PatternType.CHROMATIC:
				// RGB chromatic aberration - shifting color channels
				const chromaOffsetPrev = (this.animationTime / 300 + segmentIndex * 0.3) % 1;
				const rShiftPrev = Math.sin(chromaOffsetPrev * Math.PI * 2) * 0.5 + 0.5;
				const gShiftPrev = Math.sin((chromaOffsetPrev + 0.33) * Math.PI * 2) * 0.5 + 0.5;
				const bShiftPrev = Math.sin((chromaOffsetPrev + 0.66) * Math.PI * 2) * 0.5 + 0.5;
				const rPrev = Math.floor(255 * rShiftPrev);
				const gPrev = Math.floor(255 * gShiftPrev);
				const bPrev = Math.floor(255 * bShiftPrev);
				return `rgb(${rPrev}, ${gPrev}, ${bPrev})`;

			case PatternType.EMBER:
				// Glowing embers floating up effect
				const emberGlowPrev = Math.sin(this.animationTime / 100 + segmentIndex * 2.5) * 0.4 + 0.6;
				const emberPopPrev = Math.sin(this.animationTime / 60 + segmentIndex * 3) > 0.9;
				if (emberPopPrev) {
					return secondary;
				}
				return this.applyBrightness(primary, emberGlowPrev);

			case PatternType.HYPNOTIC:
				// Swirling hypnotic spiral effect
				const spiralPhasePrev = (this.animationTime / 250 + segmentIndex * 0.4) % 1;
				const spiralPulsePrev = Math.sin(spiralPhasePrev * Math.PI * 4) * 0.5 + 0.5;
				return segmentIndex % 2 === Math.floor(spiralPhasePrev * 2) % 2
					? this.interpolateColor(primary, secondary, spiralPulsePrev)
					: this.interpolateColor(secondary, primary, spiralPulsePrev);

			case PatternType.PIXELATE:
				// Retro pixel art shifting - blocky color changes
				const pixelBlockPrev = Math.floor(this.animationTime / 200);
				const pixelPatternPrev = (segmentIndex + pixelBlockPrev) % 4;
				switch (pixelPatternPrev) {
					case 0: return primary;
					case 1: return secondary;
					case 2: return this.interpolateColor(primary, secondary, 0.5);
					default: return primary;
				}

			// ═══════════════════════════════════════════════════════════════════
			//  🍯 NATURE & ORGANIC PATTERNS
			// ═══════════════════════════════════════════════════════════════════

			case PatternType.BEEHIVE:
				// Honeycomb shimmer effect
				const honeyCellPhasePrev = (segmentIndex + Math.floor(this.animationTime / 150)) % 6;
				const honeyShimmerPrev = Math.sin(this.animationTime / 120 + segmentIndex * 1.2) * 0.3 + 0.7;
				const isHoneyHighlightPrev = honeyCellPhasePrev === 0;
				return isHoneyHighlightPrev
					? this.interpolateColor(secondary, '#ffd700', honeyShimmerPrev)
					: this.interpolateColor(primary, secondary, t * honeyShimmerPrev);

			case PatternType.MARBLE:
				// Elegant marble veins flowing
				const marbleVeinPhasePrev = Math.sin(this.animationTime / 300 + segmentIndex * 0.8 + t * 3);
				const marbleSwirlPrev = Math.sin(this.animationTime / 200 + segmentIndex * 1.5) * 0.2 + 0.5;
				const hasVeinPrev = marbleVeinPhasePrev > 0.6;
				return hasVeinPrev
					? this.interpolateColor(secondary, primary, marbleSwirlPrev)
					: this.applyBrightness(primary, 0.9 + marbleSwirlPrev * 0.2);

			case PatternType.WOOD:
				// Natural wood grain rings
				const woodRingPhasePrev = (segmentIndex + Math.floor(this.animationTime / 400)) % 5;
				const woodGrainPrev = Math.sin(this.animationTime / 250 + segmentIndex * 0.6) * 0.15 + 0.85;
				const isRingPrev = woodRingPhasePrev < 2;
				return isRingPrev
					? this.applyBrightness(secondary, woodGrainPrev)
					: this.applyBrightness(primary, woodGrainPrev);

			case PatternType.CORAL:
				// Living coral reef texture
				const coralPulsePrev = Math.sin(this.animationTime / 180 + segmentIndex * 1.3) * 0.3 + 0.7;
				const coralBranchPrev = Math.sin(this.animationTime / 250 + segmentIndex * 2) > 0.5;
				const coralGlowPrev = Math.sin(this.animationTime / 100 + segmentIndex) * 0.2 + 0.8;
				return coralBranchPrev
					? this.applyBrightness(secondary, coralPulsePrev * coralGlowPrev)
					: this.applyBrightness(primary, coralPulsePrev);

			case PatternType.FEATHER:
				// Iridescent feather pattern
				const featherIridescencePrev = (t + this.animationTime / 400 + segmentIndex * 0.1) % 1;
				const featherSheenPrev = Math.sin(this.animationTime / 150 + segmentIndex * 2) * 0.4 + 0.6;
				const featherBlendPrev = Math.sin(featherIridescencePrev * Math.PI * 2) * 0.5 + 0.5;
				return this.interpolateColor(primary, secondary, featherBlendPrev * featherSheenPrev);

			// ═══════════════════════════════════════════════════════════════════
			//  🔌 TECH & FUTURISTIC PATTERNS
			// ═══════════════════════════════════════════════════════════════════

			case PatternType.CIRCUIT:
				// Electronic circuit board traces
				const circuitTracePrev = (this.animationTime / 80 + segmentIndex * 2) % 8;
				const circuitFlowPrev = circuitTracePrev < 2 ? 1.5 : 0.7;
				const isTraceNodePrev = segmentIndex % 4 === Math.floor(this.animationTime / 300) % 4;
				if (isTraceNodePrev) {
					return secondary;
				}
				return this.applyBrightness(primary, circuitFlowPrev);

			case PatternType.HOLOGRAM:
				// 3D hologram scan lines
				const holoScanLinePrev = (this.animationTime / 60 + segmentIndex) % 4;
				const holoFlickerPrev = Math.sin(this.animationTime / 40) * 0.3 + 0.7;
				const holoRGBPrev = Math.sin(this.animationTime / 100 + segmentIndex * 0.5) * 0.5 + 0.5;
				if (holoScanLinePrev < 1) {
					return this.interpolateColor(secondary, '#ffffff', holoFlickerPrev);
				}
				return this.interpolateColor(primary, secondary, holoRGBPrev * holoFlickerPrev);

			case PatternType.QUANTUM:
				// Quantum particle uncertainty
				const quantumPhasePrev = Math.sin(this.animationTime / 50 + segmentIndex * 4.5) * 0.5 + 0.5;
				const quantumUncertaintyPrev = Math.random() * 0.3;
				const quantumGlowPrev = 0.4 + quantumPhasePrev * 0.6 + quantumUncertaintyPrev;
				const quantumPopPrev = Math.sin(this.animationTime / 80 + segmentIndex * 3) > 0.95;
				if (quantumPopPrev) {
					return secondary;
				}
				return this.applyBrightness(primary, quantumGlowPrev);

			case PatternType.BINARY:
				// Binary code streaming
				const binaryStreamPrev = Math.floor(this.animationTime / 100 + segmentIndex * 1.5) % 2;
				const binaryGlowPrev = Math.sin(this.animationTime / 80 + segmentIndex * 2) * 0.4 + 0.6;
				const binaryBrightPrev = binaryStreamPrev === 1 ? 1.3 : 0.5;
				return this.applyBrightness(binaryStreamPrev === 1 ? secondary : primary, binaryGlowPrev * binaryBrightPrev);

			// ═══════════════════════════════════════════════════════════════════
			//  🎨 ARTISTIC PATTERNS
			// ═══════════════════════════════════════════════════════════════════

			case PatternType.MOSAIC:
				// Stained glass mosaic tiles
				const mosaicTilePrev = (segmentIndex + Math.floor(this.animationTime / 200)) % 5;
				const mosaicGlowPrev = Math.sin(this.animationTime / 150 + segmentIndex * 1.2) * 0.25 + 0.75;
				const mosaicLightPrev = Math.sin(this.animationTime / 100 + mosaicTilePrev) > 0.7;
				if (mosaicLightPrev) {
					return this.interpolateColor(secondary, '#ffffff', mosaicGlowPrev * 0.5);
				}
				return mosaicTilePrev % 2 === 0
					? this.applyBrightness(primary, mosaicGlowPrev)
					: this.applyBrightness(secondary, mosaicGlowPrev);

			case PatternType.WATERCOLOR:
				// Soft watercolor blending
				const waterBlendPrev = Math.sin(this.animationTime / 250 + segmentIndex * 0.8) * 0.5 + 0.5;
				const waterFlowPrev = Math.sin(this.animationTime / 180 + t * 4) * 0.3 + 0.5;
				const waterSoftPrev = (waterBlendPrev + waterFlowPrev) / 2;
				return this.interpolateColor(primary, secondary, waterSoftPrev);

			case PatternType.TRIBAL:
				// Bold tribal tattoo markings
				const tribalBeatPrev = Math.sin(this.animationTime / 200 + segmentIndex * 1.5) * 0.3 + 0.7;
				const tribalMarkPrev = segmentIndex % 3;
				const tribalPulsePrev = Math.sin(this.animationTime / 150) > 0.5;
				if (tribalMarkPrev === 0) {
					return this.applyBrightness(secondary, tribalBeatPrev);
				}
				return tribalPulsePrev && tribalMarkPrev === 1
					? this.interpolateColor(primary, secondary, 0.5)
					: this.applyBrightness(primary, tribalBeatPrev);

			case PatternType.ORIGAMI:
				// Paper fold geometric patterns
				const origamiFoldPrev = (segmentIndex + Math.floor(this.animationTime / 180)) % 4;
				const origamiLightPrev = Math.sin(this.animationTime / 120 + segmentIndex * 2) * 0.4 + 0.6;
				const origamiCreasePrev = origamiFoldPrev < 2;
				if (origamiCreasePrev) {
					return this.interpolateColor(primary, '#ffffff', origamiLightPrev * 0.3);
				}
				return this.interpolateColor(primary, secondary, origamiLightPrev);

			default:
				return primary;
		}
	}

	/**
	 * Get color for flag skin segment
	 */
	private getFlagSegmentColor(skin: FlagSkin, segmentIndex: number, totalSegments: number): string {
		const colors = skin.colors;
		if (colors.length === 0) return '#ffffff';
		if (colors.length === 1) return colors[0]!;

		// Map segment to color stripe
		const colorIndex = Math.floor((segmentIndex / totalSegments) * colors.length);
		return colors[Math.min(colorIndex, colors.length - 1)]!;
	}

	/**
	 * Interpolate between two hex colors
	 */
	private interpolateColor(color1: string, color2: string, t: number): string {
		const r1 = parseInt(color1.slice(1, 3), 16);
		const g1 = parseInt(color1.slice(3, 5), 16);
		const b1 = parseInt(color1.slice(5, 7), 16);

		const r2 = parseInt(color2.slice(1, 3), 16);
		const g2 = parseInt(color2.slice(3, 5), 16);
		const b2 = parseInt(color2.slice(5, 7), 16);

		const r = Math.floor(r1 + (r2 - r1) * t);
		const g = Math.floor(g1 + (g2 - g1) * t);
		const b = Math.floor(b1 + (b2 - b1) * t);

		return `rgb(${r}, ${g}, ${b})`;
	}

	/**
	 * Get rainbow color based on position
	 */
	private getRainbowColor(t: number): string {
		const hue = t * 360;
		return `hsl(${hue}, 100%, 50%)`;
	}

	/**
	 * Convert HSL to hex string
	 */
	private hslToHex(h: number, s: number, l: number): string {
		s /= 100;
		l /= 100;
		const a = s * Math.min(l, 1 - l);
		const f = (n: number) => {
			const k = (n + h / 30) % 12;
			const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
			return Math.round(255 * color).toString(16).padStart(2, '0');
		};
		return `#${f(0)}${f(8)}${f(4)}`;
	}

	/**
	 * Apply neon glow effect
	 */
	private applyNeonEffect(hexColor: string, t: number): string {
		const r = parseInt(hexColor.slice(1, 3), 16);
		const g = parseInt(hexColor.slice(3, 5), 16);
		const b = parseInt(hexColor.slice(5, 7), 16);

		// Pulse effect
		const pulse = Math.sin(this.animationTime / 200 + t * 2) * 0.3 + 0.7;
		const newR = Math.min(255, Math.floor(r * pulse));
		const newG = Math.min(255, Math.floor(g * pulse));
		const newB = Math.min(255, Math.floor(b * pulse));

		return `rgb(${newR}, ${newG}, ${newB})`;
	}

	/**
	 * Apply metallic shine effect
	 */
	private applyMetallicEffect(primary: string, secondary: string, t: number): string {
		// Metallic wave effect
		const wave = Math.sin(this.animationTime / 300 + t * 4) * 0.5 + 0.5;
		return this.interpolateColor(primary, secondary, wave);
	}

	/**
	 * Get the container element
	 */
	getElement(): HTMLDivElement {
		return this.container;
	}

	/**
	 * Get the preview canvas element (for external positioning)
	 */
	getPreviewCanvas(): HTMLCanvasElement {
		return this.previewCanvas;
	}

	/**
	 * Get the currently selected skin
	 */
	getSelectedSkin(): PlayerSkin {
		return this.selectedSkin;
	}

	/**
	 * Get serialized skin data for network transmission
	 */
	getSkinData(): SkinData {
		return serializeSkin(this.selectedSkin);
	}

	/**
	 * Get primary color as number (for legacy compatibility)
	 */
	getPrimaryColorNumber(): number {
		switch (this.selectedSkin.type) {
			case SkinType.COLOR:
				return hexToNumber(this.selectedSkin.primaryColor);
			case SkinType.PATTERN:
				return hexToNumber(this.selectedSkin.primaryColor);
			case SkinType.FLAG:
				return hexToNumber(this.selectedSkin.colors[0] || '#ffffff');
			default:
				return 0x4ecdc4;
		}
	}

	/**
	 * Set the accessory to display in the preview
	 */
	setAccessory(accessory: string): void {
		this.selectedAccessory = accessory;
	}

	/**
	 * Clean up resources
	 */
	destroy(): void {
		if (this.animationFrame) {
			cancelAnimationFrame(this.animationFrame);
			this.animationFrame = null;
		}
		this.animatedButtonCanvases.clear();
		this.colorButtonCanvases.clear();
		this.flagButtonCanvases.clear();
		this.container.remove();
	}
}
